import { useState } from 'react';
import { Mail, Send, Eye, AlertTriangle, Info, Users } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { pluralise } from '@/lib/format';
import {
  Badge, Button, Card, CardHeader, Checkbox, Field, Input, PageHeader, Select,
  Textarea, useToast,
} from '@/components/ui';
import type {
  AdminAudience, AdminEmailPreview, AdminOrgSummary, EmailTemplateList, StaffRole,
} from '@shared/types';
import { useApi } from '@/lib/useApi';

const ROLES: StaffRole[] = ['owner', 'manager', 'staff', 'viewer'];
const STATUSES = ['trialing', 'active', 'lapsed', 'cancelled'];

/**
 * Writing to customers.
 *
 * The shape of this screen is one decision: you cannot send without previewing
 * first. The preview resolves the audience on the server using the same
 * function the send uses, so the list you approved is by construction the list
 * that gets written to — not a second implementation that agrees until one of
 * them is edited.
 */
export default function EmailPage() {
  const toast = useToast();
  const { data: orgs } = useApi<AdminOrgSummary[]>('/admin/organisations');
  const { data: templateData } = useApi<EmailTemplateList>(
    '/admin/email/templates', { audience: 'customers' },
  );

  const [orgId, setOrgId] = useState('');
  const [roles, setRoles] = useState<StaffRole[]>(['owner']);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [includeDemo, setIncludeDemo] = useState(false);

  const [templateId, setTemplateId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [fromAddress, setFromAddress] = useState('');

  const [preview, setPreview] = useState<AdminEmailPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  function audience(): AdminAudience {
    return {
      org_id: orgId || undefined,
      roles,
      statuses: statuses.length ? statuses : undefined,
      include_inactive: includeInactive,
      include_demo: includeDemo,
    };
  }

  /** The audience, the words and the sender — what both calls need. */
  function request() {
    return {
      ...audience(),
      subject,
      body,
      template_id: templateId || undefined,
      from_address: fromAddress || undefined,
    };
  }

  /**
   * Loads a template into the boxes rather than sending it by reference.
   *
   * The words are then editable, and an edit is what gets sent — adjusting one
   * message should not mean editing the template everybody else uses. The
   * server applies the same rule, so the two agree.
   */
  function pickTemplate(id: string) {
    setTemplateId(id);
    invalidate();
    const chosen = (templateData?.templates ?? []).find((t) => t.id === id);
    if (chosen) {
      setSubject(chosen.subject);
      setBody(chosen.body);
      if (chosen.from_address) setFromAddress(chosen.from_address);
    }
  }

  // Any change to who or what invalidates an approval that was given for
  // something else. Cheaper to re-preview than to send the wrong thing.
  function invalidate() { setPreview(null); }

  async function runPreview() {
    setBusy(true);
    try {
      const res = await api.post<AdminEmailPreview>('/admin/email/preview', request());
      setPreview(res);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not work out the audience');
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    setSending(true);
    try {
      const res = await api.post<{
        recipients: number; sent: number; failed: number; skipped: number;
      }>('/admin/email/send', request());

      toast.success(`Sent ${res.sent} of ${res.recipients}`
        + (res.failed ? `, ${res.failed} failed` : '')
        + (res.skipped ? `, ${res.skipped} not sent — email is off` : ''));

      setPreview(null);
      setSubject('');
      setBody('');
      setTemplateId('');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not send');
    } finally {
      setSending(false);
    }
  }

  const toggle = <T extends string>(list: T[], value: T, set: (v: T[]) => void) => {
    set(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
    invalidate();
  };

  return (
    <>
      <PageHeader
        title="Email customers"
        subtitle="Goes out from this deployment's own address, never an organisation's"
      />

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <Card>
            <CardHeader title="Who" subtitle="Narrow it before you write it" />
            <div className="p-5 sm:p-6 pt-4 space-y-5">
              <Field label="Organisation" hint="Leave blank for every organisation">
                <Select
                  value={orgId}
                  onChange={(e) => { setOrgId(e.target.value); invalidate(); }}
                >
                  <option value="">Every organisation</option>
                  {(orgs ?? []).map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </Select>
              </Field>

              <div>
                <p className="text-sm font-medium text-ink mb-2">Roles</p>
                <div className="flex flex-wrap gap-2">
                  {ROLES.map((role) => (
                    <Pill
                      key={role}
                      active={roles.includes(role)}
                      onClick={() => toggle(roles, role, setRoles)}
                    >
                      {role}
                    </Pill>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-ink mb-2">Subscription status</p>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <Pill
                      key={s}
                      active={statuses.includes(s)}
                      onClick={() => toggle(statuses, s, setStatuses)}
                    >
                      {s}
                    </Pill>
                  ))}
                </div>
                <p className="text-xs text-faint mt-2">
                  None selected means every status. A trial that quietly ran out counts
                  as lapsed here, not trialing.
                </p>
              </div>

              <div className="space-y-2 pt-1">
                <Checkbox
                  label="Include deactivated accounts"
                  checked={includeInactive}
                  onChange={(e) => { setIncludeInactive(e.target.checked); invalidate(); }}
                />
                <Checkbox
                  label="Include the demo organisation"
                  description="Normally excluded — it is ours, not a customer's"
                  checked={includeDemo}
                  onChange={(e) => { setIncludeDemo(e.target.checked); invalidate(); }}
                />
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader title="What" />
            <div className="p-5 sm:p-6 pt-4 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Template" hint="Or write something below">
                  <Select value={templateId} onChange={(e) => pickTemplate(e.target.value)}>
                    <option value="">No template</option>
                    {(templateData?.templates ?? []).map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </Select>
                </Field>
                {/*
                  Account mail defaults to the deployment's own address, not to
                  the sales one. Somebody being told their plan has changed
                  should not hear from sales — and the picker only offers
                  addresses on a domain we have authenticated, because the API
                  refuses anything else outright rather than quietly swapping it.
                */}
                <Field label="From" hint="Only addresses this deployment can send from">
                  <Select
                    value={fromAddress}
                    onChange={(e) => { setFromAddress(e.target.value); invalidate(); }}
                  >
                    <option value="">Default</option>
                    {(preview?.senders ?? templateData?.senders ?? []).map((address) => (
                      <option key={address} value={address}>{address}</option>
                    ))}
                  </Select>
                </Field>
              </div>

              <Field label="Subject">
                <Input
                  value={subject}
                  onChange={(e) => { setSubject(e.target.value); invalidate(); }}
                  placeholder="A change to your PROVIA plan"
                />
              </Field>
              <Field
                label="Message"
                hint="Plain text. Placeholders in {{braces}} are filled per recipient."
              >
                <Textarea
                  value={body}
                  onChange={(e) => { setBody(e.target.value); invalidate(); }}
                  rows={12}
                  className="min-h-[16rem] font-mono text-xs"
                  placeholder={'Hello {{first_name}},\n\nYour account for {{org_name}} …'}
                />
              </Field>

              {preview && preview.variables.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {preview.variables.map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setBody((b) => `${b}{{${v}}}`)}
                      className="text-xs px-2 py-1 rounded-lg bg-raised border border-line
                                 text-muted hover:text-ink transition-colors font-mono"
                    >
                      {`{{${v}}}`}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader
              title="Check before sending"
              subtitle="Nothing goes out until you have seen this"
            />
            <div className="p-5 sm:p-6 pt-4 space-y-4">
              <Button
                variant="secondary"
                className="w-full"
                icon={<Eye className="h-4 w-4" />}
                loading={busy}
                onClick={() => void runPreview()}
              >
                Work out who gets this
              </Button>

              {preview && (
                <>
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-faint" />
                    <span className="text-ink font-medium">
                      {pluralise(preview.total, 'recipient')}
                    </span>
                    {preview.over_limit && (
                      <Badge tone="warn">over the {preview.limit} limit</Badge>
                    )}
                  </div>

                  {preview.over_limit && (
                    <div className="flex items-start gap-2 text-sm text-warn bg-warn/10
                                    border border-warn/25 rounded-xl px-3 py-2.5">
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                      <span>
                        One request may send {preview.limit}. Narrow this — by status, or
                        one organisation at a time — and send it in batches.
                      </span>
                    </div>
                  )}

                  {!preview.email_configured && (
                    <div className="flex items-start gap-2 text-sm text-warn bg-warn/10
                                    border border-warn/25 rounded-xl px-3 py-2.5">
                      <Info className="h-4 w-4 shrink-0 mt-0.5" />
                      <span>
                        Email is not switched on. Sending now would write every message to
                        the execution log instead of delivering it.
                      </span>
                    </div>
                  )}

                  <SkippedNote skipped={preview.skipped} />

                  {preview.sample && (
                    <div className="rounded-xl border border-line overflow-hidden">
                      <div className="px-3 py-2 bg-raised border-b border-line text-xs text-faint">
                        As {preview.sample.to} will see it
                      </div>
                      <div className="p-3 space-y-2">
                        <p className="text-xs text-faint">
                          From {preview.sample.from}
                        </p>
                        <p className="text-sm font-medium text-ink">
                          {preview.sample.subject || <span className="text-faint">(no subject)</span>}
                        </p>
                        <p className="text-sm text-muted whitespace-pre-wrap leading-relaxed">
                          {preview.sample.body || '(empty)'}
                        </p>
                      </div>
                    </div>
                  )}

                  {preview.recipients.length > 0 && (
                    <details className="text-sm">
                      <summary className="cursor-pointer text-muted hover:text-ink">
                        Every recipient
                      </summary>
                      <ul className="mt-2 space-y-1 max-h-64 overflow-y-auto">
                        {preview.recipients.map((r) => (
                          <li key={r.user_id} className="flex items-center justify-between gap-3">
                            <span className="text-ink truncate">{r.email}</span>
                            <span className="text-faint text-xs shrink-0">{r.org_name}</span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}

                  <Button
                    variant="primary"
                    className="w-full"
                    icon={<Send className="h-4 w-4" />}
                    loading={sending}
                    disabled={preview.total === 0 || preview.over_limit || !subject || !body}
                    onClick={() => void send()}
                  >
                    Send to {pluralise(preview.total, 'person', 'people')}
                  </Button>
                </>
              )}

              {!preview && (
                <p className="text-sm text-muted leading-relaxed">
                  The audience is worked out on the server, by the same code that does the
                  sending — so what you are shown here is what goes out.
                </p>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title="What this is not for" />
            <div className="p-5 sm:p-6 pt-4 text-sm text-muted leading-relaxed space-y-2">
              <p className="flex items-start gap-2">
                <Mail className="h-4 w-4 shrink-0 mt-0.5 text-faint" />
                Tenants. They are reached by SMS from their landlord, and nothing here can
                write to them — a tenant gave their phone number to a landlord, not to us.
              </p>
              <p>
                Every send is recorded in the email log with its subject, body and outcome,
                against your name.
              </p>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

function SkippedNote({ skipped }: { skipped: AdminEmailPreview['skipped'] }) {
  const parts: string[] = [];
  if (skipped.inactive) parts.push(`${skipped.inactive} deactivated`);
  if (skipped.no_email) parts.push(`${skipped.no_email} with no address`);
  if (skipped.filtered_by_status) parts.push(`${skipped.filtered_by_status} by status`);
  if (skipped.demo) parts.push(`${skipped.demo} in the demo account`);
  if (parts.length === 0) return null;

  return (
    <p className="text-xs text-faint">
      Not included: {parts.join(', ')}.
    </p>
  );
}

function Pill({ active, onClick, children }: {
  active: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={active
        ? 'text-xs px-2.5 py-1 rounded-full border border-brand/40 bg-brand/15 text-ink font-medium capitalize'
        : 'text-xs px-2.5 py-1 rounded-full border border-line text-muted hover:text-ink transition-colors capitalize'}
    >
      {children}
    </button>
  );
}
